import * as React from 'react'
import { StackChildren, Text } from '@doordash/design-language'
import Button from '@doordash/component-button'
import {
  RiskActions,
  RiskContext,
  RiskWorkflows,
} from '@doordash/component-risk'
import { Mode } from './types'
import { Channel } from '../../../models/types'
import { ButtonGroup } from './styles'
import {
  ANDROID_CLOSE_DEEPLINK,
  IOS_CLOSE_DEEPLINK,
  COUNTRY_SHORTCODES
} from '../../../constants'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'
import { connect } from 'react-redux'
import * as Exp from '../../../experimentConstants'
import { loadDasherSupplementalOnboarding, isMfaReady } from '../../../api'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import t from './strings'

type ReduxProps = {
  trackWebviewMultiFactorAuth: (data: DA.WebviewMultiFactorAuth) => Promise<any>
}

type Props = {
  country?: string
  experiments?: any
  setMode: React.Dispatch<React.SetStateAction<Mode>>
  setChannel: React.Dispatch<React.SetStateAction<Channel>>
}

export const VerifyPhoneNumber: React.FC<Props & ReduxProps> = ({
  country = 'US',
  experiments,
  setChannel,
  setMode,
  trackWebviewMultiFactorAuth,
}) => {
  const { dispatch } = React.useContext(RiskContext)
  const os = detectWebviewOs()
  const [checked, setChecked] = React.useState<boolean>(false)
  const [mfaReady, setMfaReady] = React.useState<boolean>(false)

  React.useEffect(() => {
    trackWebviewMultiFactorAuth({
      action: DA.EventActions.Rendered,
      name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
      country,
    })
    const checkMfaReady = async () => {
      try {
        setMfaReady(Boolean(await isMfaReady()))
        trackWebviewMultiFactorAuth({
          action: DA.EventActions.Success,
          name: DA.WebviewMultiFactorAuthNames.ReadyPhone,
          country,
        })
      } catch (e) {
        trackWebviewMultiFactorAuth({
          action: DA.EventActions.Error,
          name: DA.WebviewMultiFactorAuthNames.ReadyPhone,
          country,
        })
        setMfaReady(false)
      } finally {
        setChecked(true)
      }
    }
    if (!checked) {
      checkMfaReady()
    }
  }, [])

  const handleUpdatePhoneClicked = () => {
    trackWebviewMultiFactorAuth({
      action: DA.EventActions.Clicked,
      name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
      country,
    })
    setMode(Mode.UpdatePhone)
  }

  const trigger2FAPhone = () => {
    trackWebviewMultiFactorAuth({
      action: DA.EventActions.Clicked,
      name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
      country,
    })

    setChannel(Channel.Sms)

    dispatch({
      type: RiskActions.RegisterCallback,
      onClose: () => {
        trackWebviewMultiFactorAuth({
          action: DA.EventActions.Close,
          name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
          country,
        })
      },
      onSuccess: async () => {
        trackWebviewMultiFactorAuth({
          action: DA.EventActions.Success,
          name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
          country,
        })

        // if JP, lets exit
        if (country === COUNTRY_SHORTCODES.JP) {
          trackWebviewMultiFactorAuth({
            action: DA.EventActions.Exit,
            name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
            country,
          })
          window.location.assign(
            os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK
          )
        } else {
          const {
            data: {
              needs_supplemental_onboarding_user_experience: needsSupplementalOnboarding,
            }
          } = await loadDasherSupplementalOnboarding()

          if (needsSupplementalOnboarding || !experiments[Exp.HAS_COMPLETED_FIRST_DELIVERY]) {
            trackWebviewMultiFactorAuth({
              action: DA.EventActions.Reload,
              name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
              country,
            })
            // if dasher is still onboarding OR hasn't completed first delivery, reload webview
            window.location.reload()
          } else { // otherwise dismiss webview
            trackWebviewMultiFactorAuth({
              action: DA.EventActions.Exit,
              name: DA.WebviewMultiFactorAuthNames.VerifyPhone,
              country,
            })
            window.location.assign(
              os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK
            )
          }
        }
      },
    })

    dispatch({
      type: RiskActions.VerificationRequired,
      workflow: RiskWorkflows.DasherLogin,
    })
  }

  return (
    <StackChildren>
      <Text
        styles={Text.Styles.PageTitle}
        textAlign={Text.TextAlignments.Center}
      >
        {t.verifyPhone.title()}
      </Text>
      <Text
        styles={Text.Styles.PageSubtext}
        textAlign={Text.TextAlignments.Center}
      >
        {t.verifyPhone.subtitle()}
      </Text>
      <ButtonGroup>
        <Button onClick={trigger2FAPhone} state={checked ? Button.States.Default : Button.States.Loading} isDisabled={mfaReady === false}>
          {t.verifyPhone.primaryAction()}
        </Button>
        <Button
          type={Button.Types.FlatPrimary}
          onClick={handleUpdatePhoneClicked}
        >
          {t.verifyPhone.secondaryAction()}
        </Button>
        {checked && !mfaReady && (
          <Button
            tag={'a'}
            size={Button.Sizes.Small}
            type={Button.Types.FlatSecondary}
            onClick={() => {
              trackWebviewMultiFactorAuth({
                action: DA.EventActions.Clicked,
                name: DA.WebviewMultiFactorAuthNames.GetHelp,
                country,
              })
            }}
            href="https://www.doordash.com/help"
          >
            {t.verifyPhone.getHelp()}
          </Button>
        )}
      </ButtonGroup>
    </StackChildren>
  )
}

const mapStateToProps = state => ({
  experiments: state.experiments,
  country: state.application.country,
})

const mapDispatchToProps = {
  trackWebviewMultiFactorAuth: events.trackWebviewMultiFactorAuth,
}

export default connect(mapStateToProps, mapDispatchToProps)(VerifyPhoneNumber)
